import React, { useState, useEffect, useMemo } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { toast } from "react-hot-toast";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Modal } from "react-bootstrap";
import { isEmpty } from "lodash";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import {
    getPlayableProperties,
    setDailyPlayableProperty,
    deletePlayableProperty,
    updatePlayableProperty,
    importApprovedListings,
    type UpdatePlayablePropertyPayload,
} from "@/services/PropertyService";
import { useUser } from "@/context/UserContext";
import { useNavigate } from "react-router-dom";

import CustomButton from "@/components/common/buttons/custom-button/custom-button";
import CustomInput from "@/components/common/custom-input/custom-input";
import MoonLoader from "react-spinners/MoonLoader";
import { cstMoment } from "@common/utils/date.utils";
import { normalizeMlsId } from "@/helpers/helpers";
import { useAdminAccess } from "@/hooks/useAdminAccess";
import AdminAccessDenied from "../AdminAccessDenied";
import { logger } from "@/services/LoggerService";

type PlayableProperty = any;

export default function SetPuzzleAdmin(): React.ReactElement {
    const { isLoading: authLoading } = useAuth0();
    const { userState } = useUser();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const { hasAdminAccess, isLoading: adminAccessLoading } = useAdminAccess();

    // Pagination state
    const [page, setPage] = useState<number>(1);
    const limit = 20;

    // Date filter state for Scheduled Properties table
    const [startDateFilter, setStartDateFilter] = useState<Date | null>(null);
    const [endDateFilter, setEndDateFilter] = useState<Date | null>(null);

    // Helper to format Date object to YYYY-MM-DD using its calendar components
    // This avoids timezone shifts from converting the Date again, so selecting Feb 11 always yields "2026-02-11"
    const formatDateOnly = (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    // Derived filter values in YYYY-MM-DD format (date-only; backend interprets in CST)
    const minPlayDateFilter = useMemo(
        () => (startDateFilter ? formatDateOnly(startDateFilter) : undefined),
        [startDateFilter],
    );
    const maxPlayDateFilter = useMemo(
        () => (endDateFilter ? formatDateOnly(endDateFilter) : undefined),
        [endDateFilter],
    );

    // Reset to first page whenever filters change
    useEffect(() => {
        setPage(1);
    }, [minPlayDateFilter, maxPlayDateFilter]);

    // Query playable properties (includeFuture: true so date range can show future scheduled properties)
    const { data: playablePropertiesData, isLoading: isLoadingProperties } = useQuery({
        queryKey: [
            "admin-playable-properties",
            { minPlayDate: minPlayDateFilter, maxPlayDate: maxPlayDateFilter, page, limit, includeFuture: true },
        ],
        queryFn: () =>
            getPlayableProperties({
                minPlayDate: minPlayDateFilter,
                maxPlayDate: maxPlayDateFilter,
                page,
                limit,
                includeFuture: true,
            } as any),
        refetchOnMount: true,
        refetchOnWindowFocus: false,
        staleTime: 0, // To ensure fresh data on refetch
    });

    // Mutation for setting daily property
    const setDailyPropertyMutation = useMutation({
        mutationFn: ({
            playDate,
            mlsId,
            imageIndex,
            url,
            listingAgent,
            propertyData,
            emailPuzzleSubject,
            emailPuzzleImage,
            emailPuzzleBody,
            featureAgent,
            externalPropertyLink,
        }: {
            playDate: string;
            mlsId: string;
            imageIndex?: number;
            url: string;
            listingAgent?: any;
            propertyData?: any;
            emailPuzzleSubject?: string;
            emailPuzzleImage?: number;
            emailPuzzleBody?: string;
            featureAgent?: boolean;
            externalPropertyLink?: string;
        }) => {
            const normalizedMlsIdValue = normalizeMlsId(mlsId);

            // Prepare propertyData with externalPropertyLink if provided
            // If propertyData is provided, add default values for required fields to pass validation
            const hasPropertyData = propertyData && Object.keys(propertyData).some((key) => propertyData[key]);
            const finalPropertyData = hasPropertyData
                ? {
                      // Default values for required fields (will be overridden by actual values if provided)
                      streetAddress: "",
                      city: "",
                      state: "",
                      zipCode: "",
                      neighborhood: "",
                      listPrice: 0,
                      // Include provided propertyData (may override defaults)
                      ...(propertyData || {}),
                      // Include externalPropertyLink if provided
                      ...(externalPropertyLink && externalPropertyLink.trim()
                          ? { externalPropertyLink: externalPropertyLink.trim() }
                          : {}),
                  }
                : externalPropertyLink && externalPropertyLink.trim()
                  ? { externalPropertyLink: externalPropertyLink.trim() }
                  : undefined;

            return setDailyPlayableProperty({
                playDate,
                ...(normalizedMlsIdValue && { mlsId: normalizedMlsIdValue }),
                ...(!isEmpty(url) && { url }),
                image_index: imageIndex ? parseInt(String(imageIndex)) : 1,
                listingAgent:
                    listingAgent && Object.keys(listingAgent).some((key) => listingAgent[key])
                        ? listingAgent
                        : undefined,
                propertyData:
                    finalPropertyData && Object.keys(finalPropertyData).length > 0 ? finalPropertyData : undefined,
                emailPuzzleSubject: emailPuzzleSubject || undefined,
                emailPuzzleImage: emailPuzzleImage ? parseInt(String(emailPuzzleImage)) : undefined,
                emailPuzzleBody: emailPuzzleBody || undefined,
                featureAgent: featureAgent !== undefined ? featureAgent : undefined,
            } as any);
        },
        onSuccess: async () => {
            setShowSuccessModal(true);
            resetForm();
            // Invalidate all admin-playable-properties queries
            await queryClient.invalidateQueries({
                queryKey: ["admin-playable-properties"],
            });
            // Force refetch active queries
            await queryClient.refetchQueries({
                queryKey: ["admin-playable-properties"],
                type: "active",
            });
        },
        onError: (error: any) => {
            console.error("Error setting daily property:", error);
            logger.error("Error setting daily property", error, {
                context: "SetPuzzleAdmin",
                userId: userState?.id,
            });
            toast.error(error.response?.data?.message || "Failed to set daily property");
        },
    });

    // Mutation for deleting playable property
    const deletePropertyMutation = useMutation({
        mutationFn: (id: string) => deletePlayableProperty(id),
        onSuccess: async (_, deletedId: string) => {
            setShowDeleteModal(false);

            // Optimistically remove from local state immediately
            setPlayableProperties((prev) => prev.filter((p: any) => p.id !== deletedId));

            toast.success("Playable property deleted successfully");

            // Invalidate and refetch all matching queries
            await queryClient.refetchQueries({
                queryKey: ["admin-playable-properties"],
            });
        },
        onError: (error: any, deletedId: string) => {
            console.error("Error deleting playable property:", error);
            logger.error("Error deleting playable property", error, {
                context: "SetPuzzleAdmin",
                deletedId,
                userId: userState?.id,
            });
            toast.error(error.response?.data?.message || "Failed to delete playable property");
            // Refetch on error to ensure state is correct
            queryClient.refetchQueries({
                queryKey: ["admin-playable-properties"],
            });
        },
    });

    const updatePropertyMutation = useMutation({
        mutationFn: ({ id, payload }: { id: string; payload: UpdatePlayablePropertyPayload }) =>
            updatePlayableProperty(id, payload),
        onSuccess: async () => {
            setShowSuccessModal(true);
            setExistingProperty(null);
            resetForm();
            await queryClient.invalidateQueries({
                queryKey: ["admin-playable-properties"],
            });
            await queryClient.refetchQueries({
                queryKey: ["admin-playable-properties"],
                type: "active",
            });
        },
        onError: (error: any) => {
            console.error("Error updating daily property:", error);
            logger.error("Error updating daily property", error, {
                context: "SetPuzzleAdmin",
                userId: userState?.id,
            });
            toast.error(error.response?.data?.message || "Failed to update daily property");
        },
    });

    // Mutation for importing approved pipeline listings (runs in the background on the server)
    const importApprovedMutation = useMutation({
        mutationFn: importApprovedListings,
        onSuccess: (result) => {
            toast.success(result?.message || "Import started — scheduled listings will appear as they finish");
            // Imports take a few minutes per listing; refresh the schedule once a first batch could be done
            setTimeout(() => {
                queryClient.refetchQueries({ queryKey: ["admin-playable-properties"] });
            }, 120000);
        },
        onError: (error: any) => {
            console.error("Error starting pipeline import:", error);
            logger.error("Error starting pipeline import", error, {
                context: "SetPuzzleAdmin",
                userId: userState?.id,
            });
            toast.error(error.response?.data?.message || "Failed to start pipeline import");
        },
    });

    // Local state for table data
    const [playableProperties, setPlayableProperties] = useState<PlayableProperty[]>([]);

    useEffect(() => {
        // Always update local state from query data
        if (playablePropertiesData?.data) {
            if (playablePropertiesData.data.data && Array.isArray(playablePropertiesData.data.data)) {
                // Paginated response
                setPlayableProperties(playablePropertiesData.data.data);
            } else if (Array.isArray(playablePropertiesData.data)) {
                // Array response
                setPlayableProperties(playablePropertiesData.data as PlayableProperty[]);
            }
        } else {
            // Clear state if no data
            setPlayableProperties([]);
        }
    }, [playablePropertiesData]);

    // Form state
    const [formData, setFormData] = useState<{
        playDate: Date | null;
        mlsId: string;
        redfinLink: string;
        imageIndex: string;
        emailPuzzleSubject: string;
        emailPuzzleImage: string;
        emailPuzzleBody: string;
        featureAgent: boolean | null;
        externalPropertyLink: string;
    }>({
        playDate: new Date(),
        mlsId: "",
        redfinLink: "",
        imageIndex: "",
        emailPuzzleSubject: "",
        emailPuzzleImage: "",
        emailPuzzleBody: "",
        featureAgent: null,
        externalPropertyLink: "",
    });

    // Agent collapsible section state
    const [showAgentSection, setShowAgentSection] = useState<boolean>(false);
    const [agentData, setAgentData] = useState<{ [key: string]: string | undefined }>({
        agentFirstName: undefined,
        agentLastName: undefined,
        agentEmail: undefined,
        agentPhoneNumber: undefined,
        agentBrokerage: undefined,
        agentPrimaryLink: undefined,
        agentImageLink: undefined,
        agentInstagram: undefined,
        agentTiktok: undefined,
        agentTwitter: undefined,
        agentFacebook: undefined,
        agentYoutube: undefined,
        agentMlsId: undefined,
    });

    // Property data state
    const [propertyData, setPropertyData] = useState<{ [key: string]: string | undefined }>({
        streetAddress: undefined,
        city: undefined,
        state: undefined,
        zipCode: undefined,
        neighborhood: undefined,
        publicRemarks: undefined,
        listPrice: undefined,
        bedrooms: undefined,
        bathrooms: undefined,
        propertyType: undefined,
        squareFootage: undefined,
        parkingSpots: undefined,
        parkingType: undefined,
        yearBuilt: undefined,
        exteriorFeatures: undefined,
        interiorFeatures: undefined,
        heating: undefined,
        cooling: undefined,
        laundryFeatures: undefined,
        lotDimensions: undefined,
        lat: undefined,
        lng: undefined,
        imageUrls: undefined,
    });

    // Modal states
    const [showOverwriteModal, setShowOverwriteModal] = useState<boolean>(false);
    const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
    const [propertyToDelete, setPropertyToDelete] = useState<{ id: string; address: string; date: string } | null>(
        null,
    );
    const [existingProperty, setExistingProperty] = useState<{ id: string; address: string; date: string } | null>(
        null,
    );

    // Form handlers
    const handleInputChange = (field: keyof typeof formData, value: any) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const resetForm = () => {
        setFormData({
            playDate: new Date(),
            mlsId: "",
            redfinLink: "",
            imageIndex: "",
            emailPuzzleSubject: "",
            emailPuzzleImage: "",
            emailPuzzleBody: "",
            featureAgent: null,
            externalPropertyLink: "",
        });
        setAgentData({
            agentFirstName: undefined,
            agentLastName: undefined,
            agentEmail: undefined,
            agentPhoneNumber: undefined,
            agentBrokerage: undefined,
            agentPrimaryLink: undefined,
            agentImageLink: undefined,
            agentInstagram: undefined,
            agentTiktok: undefined,
            agentTwitter: undefined,
            agentFacebook: undefined,
            agentYoutube: undefined,
            agentMlsId: undefined,
        });
        setPropertyData({
            streetAddress: undefined,
            city: undefined,
            state: undefined,
            zipCode: undefined,
            neighborhood: undefined,
            publicRemarks: undefined,
            listPrice: undefined,
            bedrooms: undefined,
            bathrooms: undefined,
            propertyType: undefined,
            squareFootage: undefined,
            parkingSpots: undefined,
            parkingType: undefined,
            yearBuilt: undefined,
            exteriorFeatures: undefined,
            interiorFeatures: undefined,
            heating: undefined,
            cooling: undefined,
            laundryFeatures: undefined,
            lotDimensions: undefined,
            lat: undefined,
            lng: undefined,
            imageUrls: undefined,
        });
    };

    // Pagination handlers
    const handleNextPage = () => {
        if (playablePropertiesData?.data?.hasNextPage) {
            setPage((prev) => prev + 1);
        }
    };

    const handlePrevPage = () => {
        if (page > 1) {
            setPage((prev) => prev - 1);
        }
    };

    // Update handleSubmit to make image_index optional
    const handleSubmit = (e?: React.FormEvent<HTMLFormElement>) => {
        // Prevent default form submission
        if (e) {
            e.preventDefault();
        }

        if (!formData.playDate) {
            toast.error("Please select a date");
            return;
        }

        if (isEmpty(formData.mlsId) && isEmpty(formData.redfinLink)) {
            toast.error("Please provide an MLS ID or Redfin Link");
            return;
        }

        const selectedDate = cstMoment(formData.playDate as Date)
            .startOf("day")
            .format("YYYY-MM-DD HH:mm:ss");
        const selectedDateOnly = cstMoment(formData.playDate as Date).format("YYYY-MM-DD");
        // Check if property already exists for this date
        const existing = playableProperties.find(
            (pp: any) => cstMoment(pp.playDate).format("YYYY-MM-DD") === selectedDateOnly,
        );

        if (existing) {
            setExistingProperty({
                id: existing.id,
                address: existing.property?.fullAddress || existing.property?.streetAddress || "Unknown address",
                date: selectedDateOnly,
            });
            setShowOverwriteModal(true);
            return;
        }

        // Use mutation to set daily property
        setDailyPropertyMutation.mutate({
            playDate: selectedDate,
            mlsId: formData.mlsId,
            url: formData.redfinLink,
            imageIndex: formData.imageIndex ? parseInt(formData.imageIndex) : 1,
            listingAgent: agentData,
            propertyData: propertyData,
            emailPuzzleSubject: formData.emailPuzzleSubject || undefined,
            emailPuzzleImage: formData.emailPuzzleImage ? parseInt(formData.emailPuzzleImage) : undefined,
            emailPuzzleBody: formData.emailPuzzleBody || undefined,
            featureAgent: formData.featureAgent !== null ? formData.featureAgent : undefined,
            externalPropertyLink: formData.externalPropertyLink || undefined,
        });
    };

    const handleOverwrite = () => {
        if (!existingProperty) {
            setShowOverwriteModal(false);
            return;
        }

        setShowOverwriteModal(false);

        const selectedDate = cstMoment(formData.playDate as Date)
            .startOf("day")
            .format("YYYY-MM-DD HH:mm:ss");
        const normalizedMlsId = normalizeMlsId(formData.mlsId);

        // featureAgent is already a boolean or null
        const parsedFeatureAgent = formData.featureAgent !== null ? formData.featureAgent : undefined;

        // Prepare propertyData with externalPropertyLink if provided
        const finalPropertyData = {
            ...(propertyData && Object.keys(propertyData).some((key) => propertyData[key]) ? propertyData : {}),
            ...(formData.externalPropertyLink && formData.externalPropertyLink.trim()
                ? { externalPropertyLink: formData.externalPropertyLink.trim() }
                : {}),
        };

        updatePropertyMutation.mutate({
            id: existingProperty.id,
            payload: {
                playDate: selectedDate,
                ...(normalizedMlsId && { mlsId: normalizedMlsId }),
                ...(formData.redfinLink && { url: formData.redfinLink }),
                ...(formData.imageIndex && { imageIndex: formData.imageIndex }),
                listingAgent: agentData,
                propertyData: Object.keys(finalPropertyData).length > 0 ? finalPropertyData : undefined,
                ...(formData.emailPuzzleSubject && { emailPuzzleSubject: formData.emailPuzzleSubject }),
                ...(formData.emailPuzzleImage && { emailPuzzleImage: formData.emailPuzzleImage }),
                ...(formData.emailPuzzleBody && { emailPuzzleBody: formData.emailPuzzleBody }),
                ...(parsedFeatureAgent !== undefined && { featureAgent: parsedFeatureAgent }),
            },
        });
    };

    const handleScheduleAnother = () => {
        setShowSuccessModal(false);
        resetForm();
    };

    // Update form validation to only require MLS ID, date and redfinLink
    const isFormValid = () => {
        return !!formData.playDate && (!!formData.mlsId || !!formData.redfinLink);
    };

    // Compute first date without a scheduled property and default the picker to it
    const scheduledDates = useMemo(
        () => new Set((playableProperties || []).map((p: any) => cstMoment(p.playDate).format("YYYY-MM-DD"))),
        [playableProperties],
    );

    useEffect(() => {
        if (!scheduledDates) return;

        const todayCst = cstMoment().startOf("day");

        // If there are no scheduled dates, default to today
        if (scheduledDates.size === 0) {
            setFormData((prev) => ({ ...prev, playDate: todayCst.toDate() }));
            return;
        }

        // Find the latest scheduled date
        const latestScheduled = Array.from(scheduledDates)
            .map((d) => cstMoment(d).startOf("day"))
            .sort((a, b) => a.valueOf() - b.valueOf())
            .pop();

        let candidate = (latestScheduled ?? todayCst).clone().add(1, "day");

        // Ensure candidate is not in the past
        if (candidate.isBefore(todayCst)) {
            candidate = todayCst.clone();
        }

        // If for any reason the candidate date is already scheduled, move forward until an open day is found
        let guard = 0;
        while (scheduledDates.has(candidate.format("YYYY-MM-DD")) && guard < 366) {
            candidate.add(1, "day");
            guard++;
        }

        setFormData((prev) => ({ ...prev, playDate: candidate.toDate() }));
    }, [scheduledDates]);

    // Loading state
    if (authLoading || adminAccessLoading) {
        return (
            <div className="tw-flex tw-items-center tw-justify-center tw-h-screen">
                <MoonLoader size={40} color="#63c19f" />
            </div>
        );
    }

    // Not authenticated or not admin/editor
    if (!hasAdminAccess) {
        return <AdminAccessDenied redirectPath="/admin/set-puzzle" />;
    }

    return (
        <div className="tw-min-h-screen tw-bg-gray-50 tw-p-4">
            <div className="tw-max-w-7xl tw-mx-auto">
                <h1 className="tw-text-3xl tw-font-bold tw-text-gray-900 tw-mb-8">PriceMe Puzzle Schedule</h1>

                <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-[1fr_380px] tw-gap-8">
                    {/* Properties Table */}
                    <div className="tw-bg-white tw-rounded-lg tw-shadow tw-p-6 tw-min-w-0">
                        <div className="tw-flex tw-flex-wrap tw-justify-between tw-items-center tw-gap-2 tw-mb-4">
                            <h2 className="tw-text-xl tw-font-semibold">Scheduled Properties</h2>
                            <CustomButton
                                className="tw-px-4 tw-py-2 tw-text-sm tw-bg-blue-600 hover:tw-bg-blue-700 tw-text-white tw-rounded"
                                handleClick={() => importApprovedMutation.mutate()}
                                text={
                                    importApprovedMutation.isPending ? "Starting import..." : "Import approved listings"
                                }
                                isDisabled={importApprovedMutation.isPending}
                                dataCy="import-approved-listings-btn"
                                id="import-approved-listings-btn"
                            />
                        </div>

                        {/* Date Range Filters */}
                        <div className="tw-flex tw-flex-wrap tw-items-end tw-gap-4 tw-mb-4">
                            <div>
                                <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
                                    Start Date
                                </label>
                                <DatePicker
                                    selected={startDateFilter}
                                    onChange={(date) => setStartDateFilter(date)}
                                    dateFormat="MM/dd/yyyy"
                                    isClearable
                                    placeholderText="MM/DD/YYYY"
                                    className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500"
                                    allowSameDay={true}
                                />
                            </div>
                            <div>
                                <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
                                    End Date
                                </label>
                                <DatePicker
                                    selected={endDateFilter}
                                    onChange={(date) => setEndDateFilter(date)}
                                    dateFormat="MM/dd/yyyy"
                                    isClearable
                                    placeholderText="MM/DD/YYYY"
                                    className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500"
                                    allowSameDay={true}
                                    minDate={startDateFilter || undefined}
                                />
                            </div>
                            <div className="tw-flex tw-items-center tw-gap-2 tw-ml-auto tw-mb-1">
                                <button
                                    type="button"
                                    className="tw-text-sm tw-text-gray-700 tw-bg-gray-100 hover:tw-bg-gray-200 tw-border tw-border-gray-300 tw-rounded-md tw-px-3 tw-py-2"
                                    onClick={() => {
                                        setStartDateFilter(null);
                                        setEndDateFilter(null);
                                        setPage(1);
                                    }}
                                >
                                    Clear
                                </button>
                            </div>
                        </div>

                        {isLoadingProperties ? (
                            <div className="tw-flex tw-justify-center tw-py-8">
                                <MoonLoader size={30} color="#63c19f" />
                            </div>
                        ) : (
                            <>
                                <div className="tw-overflow-x-auto">
                                    <table className="tw-w-full tw-text-sm">
                                        <thead>
                                            <tr className="tw-border-b tw-border-gray-200">
                                                <th className="tw-text-left tw-py-3 tw-px-2 tw-whitespace-nowrap tw-min-w-[11rem]">
                                                    Date
                                                </th>
                                                <th className="tw-text-left tw-py-3 tw-px-2">Property Address</th>
                                                <th className="tw-text-left tw-py-3 tw-px-2">Link</th>
                                                <th className="tw-text-left tw-py-3 tw-px-2">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {playableProperties
                                                .filter((property: any) => {
                                                    const dateStr = cstMoment(property.playDate).format("YYYY-MM-DD");
                                                    if (minPlayDateFilter && dateStr < minPlayDateFilter) return false;
                                                    if (maxPlayDateFilter && dateStr > maxPlayDateFilter) return false;
                                                    return true;
                                                })
                                                .map((property: any) => (
                                                    <tr key={property.id} className="tw-border-b tw-border-gray-100">
                                                        <td className="tw-py-3 tw-px-2 tw-whitespace-nowrap tw-min-w-[11rem]">
                                                            {cstMoment(property.playDate).format("YYYY-MM-DD HH:mm:ss")}
                                                        </td>
                                                        <td className="tw-py-3 tw-px-2 tw-max-w-xs tw-truncate">
                                                            {property.property?.fullAddress ||
                                                                property.property?.streetAddress ||
                                                                "Unknown address"}
                                                        </td>
                                                        <td className="tw-py-3 tw-px-2">
                                                            <a
                                                                href={`/puzzle/${property.shortLink}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="tw-text-blue-500 hover:tw-text-blue-700 tw-underline"
                                                            >
                                                                {property.shortLink}
                                                            </a>
                                                        </td>
                                                        <td className="tw-py-3 tw-px-2">
                                                            <div className="tw-flex tw-gap-2">
                                                                <button
                                                                    type="button"
                                                                    className="tw-text-gray-600 hover:tw-text-gray-900 tw-bg-white tw-rounded tw-border tw-border-gray-200 tw-px-2 tw-py-1"
                                                                    onClick={() => {
                                                                        const address =
                                                                            property.property?.fullAddress ||
                                                                            property.property?.streetAddress ||
                                                                            "unknown-address";
                                                                        const slug = `${address.replace(/\s+/g, "-")}-${property.shortLink}`;
                                                                        navigate(
                                                                            `/admin/property/${encodeURIComponent(slug)}`,
                                                                        );
                                                                    }}
                                                                    aria-label="Edit property"
                                                                    title="Edit property"
                                                                >
                                                                    ✏️
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className="tw-text-red-600 hover:tw-text-red-800 tw-bg-white tw-rounded tw-border tw-border-red-200 tw-px-2 tw-py-1 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                                                                    onClick={() => {
                                                                        setPropertyToDelete({
                                                                            id: property.id as string,
                                                                            address:
                                                                                property.property?.fullAddress ||
                                                                                property.property?.streetAddress ||
                                                                                "Unknown address",
                                                                            date: cstMoment(property.playDate).format(
                                                                                "YYYY-MM-DD HH:mm:ss",
                                                                            ),
                                                                        });
                                                                        setShowDeleteModal(true);
                                                                    }}
                                                                    aria-label="Delete property"
                                                                    title="Delete playable property"
                                                                >
                                                                    🗑️
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            {playableProperties.length === 0 && (
                                                <tr>
                                                    <td colSpan={4} className="tw-text-center tw-py-8 tw-text-gray-500">
                                                        No scheduled properties found
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Pagination Controls */}
                                {playablePropertiesData?.data && (
                                    <div className="tw-flex tw-justify-between tw-items-center tw-mt-4 tw-px-2">
                                        <div className="tw-text-sm tw-text-gray-600">
                                            Page {page} of {Math.ceil(playablePropertiesData.data.total / limit)}
                                        </div>
                                        <div className="tw-flex tw-gap-2">
                                            <CustomButton
                                                className="tw-px-3 tw-py-1 tw-text-sm tw-bg-gray-200 hover:tw-bg-gray-300 tw-text-gray-700 tw-rounded"
                                                handleClick={handlePrevPage}
                                                text="Previous"
                                                isDisabled={page <= 1}
                                                dataCy="prev-page-btn"
                                                id="prev-page-btn"
                                            />
                                            <CustomButton
                                                className="tw-px-3 tw-py-1 tw-text-sm tw-bg-gray-200 hover:tw-bg-gray-300 tw-text-gray-700 tw-rounded"
                                                handleClick={handleNextPage}
                                                text="Next"
                                                isDisabled={!playablePropertiesData.data.hasNextPage}
                                                dataCy="next-page-btn"
                                                id="next-page-btn"
                                            />
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {/* Form */}
                    <div className="tw-bg-white tw-rounded-lg tw-shadow tw-p-6">
                        <div className="tw-flex tw-items-center tw-justify-between tw-mb-6">
                            <h2 className="tw-text-xl tw-font-semibold">Set Daily Property</h2>
                            <button
                                type="button"
                                className="tw-text-sm tw-text-blue-600 hover:tw-text-blue-800 tw-bg-white tw-border tw-border-blue-200 tw-rounded tw-px-3 tw-py-1"
                                onClick={() => navigate("/admin/property/add")}
                            >
                                Add Manually
                            </button>
                        </div>

                        <form className="tw-space-y-6" onSubmit={handleSubmit}>
                            {/* Date Input */}
                            <div>
                                <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
                                    Date
                                </label>
                                <DatePicker
                                    selected={formData.playDate}
                                    onChange={(date) => handleInputChange("playDate", date)}
                                    minDate={new Date()}
                                    dateFormat="MM/dd/yyyy"
                                    className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500"
                                />
                                <p className="tw-text-xs tw-text-gray-500 tw-mt-1">
                                    This property will be available at priceme.game/puzzle at 12am CT
                                </p>
                            </div>

                            {/* MLS ID Input */}
                            <div>
                                <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
                                    MLS ID
                                </label>
                                <CustomInput
                                    type="text"
                                    value={formData.mlsId}
                                    onChange={(e: any) => handleInputChange("mlsId", e.target.value)}
                                    placeholder="Enter MLS ID"
                                    disabled={!isEmpty(formData.redfinLink)}
                                    className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500"
                                    tabIndex={1}
                                    onClear={() => handleInputChange("mlsId", "")}
                                />
                                <p className="tw-text-xs tw-text-gray-500 tw-mt-1">For properties in or near Chicago</p>
                            </div>

                            {/* Redfin Link Input */}
                            <div>
                                <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
                                    Redfin Link
                                </label>
                                <CustomInput
                                    type="url"
                                    value={formData.redfinLink}
                                    onChange={(e: any) => handleInputChange("redfinLink", e.target.value)}
                                    placeholder="Enter Redfin URL"
                                    disabled={!isEmpty(formData.mlsId)}
                                    className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 tw-bg-gray-100"
                                    tabIndex={2}
                                    onClear={() => handleInputChange("redfinLink", "")}
                                />
                                <p className="tw-text-xs tw-text-gray-500 tw-mt-1">
                                    For properties NOT in or near Chicago
                                </p>
                            </div>

                            {/* Image Index Input */}
                            <div>
                                <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
                                    Primary Image (Optional)
                                </label>
                                <CustomInput
                                    type="number"
                                    value={formData.imageIndex}
                                    onChange={(e: any) => handleInputChange("imageIndex", e.target.value)}
                                    placeholder="Enter image index"
                                    min="1"
                                    className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500"
                                    tabIndex={3}
                                    onClear={() => handleInputChange("imageIndex", "")}
                                />
                                <p className="tw-text-xs tw-text-gray-500 tw-mt-1">
                                    Enter a whole number that corresponds to best picture of this property
                                </p>
                            </div>

                            {/* Feature Agent Input */}
                            <div>
                                <label className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData.featureAgent === true}
                                        onChange={(e) => {
                                            // Toggle between true and false
                                            if (e.target.checked) {
                                                handleInputChange("featureAgent", true);
                                            } else {
                                                handleInputChange("featureAgent", false);
                                            }
                                        }}
                                        className="tw-w-4 tw-h-4 tw-text-blue-600 tw-bg-gray-100 tw-border-gray-300 tw-rounded focus:tw-ring-blue-500"
                                        tabIndex={4}
                                    />
                                    <span className="tw-text-sm tw-font-medium tw-text-gray-700">Feature Agent</span>
                                </label>
                            </div>

                            {/* External Property Link Input */}
                            <div>
                                <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
                                    External Property Link
                                </label>
                                <CustomInput
                                    type="url"
                                    value={formData.externalPropertyLink}
                                    onChange={(e: any) => handleInputChange("externalPropertyLink", e.target.value)}
                                    placeholder="Enter external property link URL"
                                    className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500"
                                    tabIndex={5}
                                    onClear={() => handleInputChange("externalPropertyLink", "")}
                                />
                                <p className="tw-text-xs tw-text-gray-500 tw-mt-1">
                                    Optional: External link to the property listing
                                </p>
                            </div>

                            {/* Add Listing Agent - Collapsible */}
                            <div className="tw-border tw-border-gray-200 tw-rounded-md">
                                <button
                                    type="button"
                                    className="tw-w-full tw-flex tw-items-center tw-justify-between tw-px-3 tw-py-2 tw-bg-gray-50 tw-text-left tw-text-sm tw-font-medium tw-text-gray-700"
                                    onClick={() => setShowAgentSection((s) => !s)}
                                >
                                    Add Listing Agent
                                    <span>{showAgentSection ? "▲" : "▼"}</span>
                                </button>
                                {showAgentSection && (
                                    <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4 tw-p-3">
                                        {(() => {
                                            const toTitleCase = (str: string) =>
                                                str.replace(
                                                    /\w\S*/g,
                                                    (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase(),
                                                );
                                            const keyToWords = (key: string) => {
                                                const withoutPrefix = key.replace(/^agent/i, "");
                                                const spaced = withoutPrefix
                                                    .replace(/([A-Z])/g, " $1")
                                                    .trim()
                                                    .replace(/\s+/g, " ");
                                                // Ensure MLS acronym stays uppercased
                                                return toTitleCase(spaced).replace(/\bMls\b/g, "MLS");
                                            };

                                            const agentFields: string[] = [
                                                "agentFirstName",
                                                "agentLastName",
                                                "agentEmail",
                                                "agentPhoneNumber",
                                                "agentBrokerage",
                                                "agentPrimaryLink",
                                                "agentImageLink",
                                                "agentInstagram",
                                                "agentTiktok",
                                                "agentTwitter",
                                                "agentFacebook",
                                                "agentYoutube",
                                                "agentMlsId",
                                            ];

                                            return agentFields.map((key) => {
                                                const words = keyToWords(key);
                                                const label = `Agent ${words}`;
                                                const placeholder = words;
                                                return (
                                                    <div key={key} className="tw-flex tw-flex-col">
                                                        <label className="tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
                                                            {label}
                                                        </label>
                                                        <input
                                                            type="text"
                                                            value={(agentData as any)[key] || ""}
                                                            onChange={(e) =>
                                                                setAgentData((prev) => ({
                                                                    ...prev,
                                                                    [key]: e.target.value,
                                                                }))
                                                            }
                                                            placeholder={placeholder}
                                                            className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500"
                                                        />
                                                    </div>
                                                );
                                            });
                                        })()}
                                    </div>
                                )}
                            </div>

                            {/* Submit Button */}
                            <CustomButton
                                className="tw-w-full tw-bg-blue-600 hover:tw-bg-blue-700 tw-text-white tw-font-medium tw-py-2 tw-px-4 tw-rounded-md tw-transition-colors"
                                isDisabled={
                                    !isFormValid() ||
                                    (setDailyPropertyMutation as any).isPending ||
                                    (updatePropertyMutation as any).isPending
                                }
                                text={
                                    (setDailyPropertyMutation as any).isPending ||
                                    (updatePropertyMutation as any).isPending
                                        ? "Please wait..."
                                        : "Submit"
                                }
                                dataCy="submit-property-btn"
                                id="submit-property-btn"
                                type="submit"
                            />
                        </form>
                    </div>
                </div>
            </div>

            {/* Overwrite Confirmation Modal */}
            <Modal
                show={showOverwriteModal}
                onHide={() => {
                    setShowOverwriteModal(false);
                    setExistingProperty(null);
                }}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Overwrite</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        {existingProperty?.address} is already the daily property for{" "}
                        {cstMoment(formData.playDate).format("MMM D, YYYY")}. Are you sure you want to overwrite this?
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <CustomButton
                        className="tw-bg-gray-500 hover:tw-bg-gray-600 tw-text-white"
                        handleClick={() => {
                            setShowOverwriteModal(false);
                            setExistingProperty(null);
                        }}
                        text="No"
                        isDisabled={
                            (setDailyPropertyMutation as any).isPending || (updatePropertyMutation as any).isPending
                        }
                        dataCy="overwrite-no-btn"
                        id="overwrite-no-btn"
                    />
                    <CustomButton
                        className="tw-bg-red-600 hover:tw-bg-red-700 tw-text-white"
                        handleClick={handleOverwrite}
                        text={(updatePropertyMutation as any).isPending ? "Updating..." : "Yes"}
                        isDisabled={
                            (setDailyPropertyMutation as any).isPending || (updatePropertyMutation as any).isPending
                        }
                        dataCy="overwrite-yes-btn"
                        id="overwrite-yes-btn"
                    />
                </Modal.Footer>
            </Modal>

            {/* Success Modal */}
            <Modal show={showSuccessModal} onHide={() => setShowSuccessModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Success</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Daily property was successfully set.</p>
                </Modal.Body>
                <Modal.Footer>
                    <CustomButton
                        className="tw-bg-blue-600 hover:tw-bg-blue-700 tw-text-white"
                        handleClick={handleScheduleAnother}
                        text="Schedule Another Property"
                        dataCy="success-schedule-another-btn"
                        id="success-schedule-another-btn"
                    />
                </Modal.Footer>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Delete</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        Are you sure you want to delete the playable property for{" "}
                        <strong>{propertyToDelete?.address}</strong> scheduled on{" "}
                        <strong>{propertyToDelete?.date}</strong>?
                    </p>
                    <p className="tw-text-sm tw-text-gray-600 tw-mt-2">
                        Note: This will only delete the playable property association. The property record itself will
                        remain.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <CustomButton
                        className="tw-bg-gray-500 hover:tw-bg-gray-600 tw-text-white"
                        handleClick={() => setShowDeleteModal(false)}
                        text="Cancel"
                        isDisabled={(deletePropertyMutation as any).isPending}
                        dataCy="delete-cancel-btn"
                        id="delete-cancel-btn"
                    />
                    <CustomButton
                        className="tw-bg-red-600 hover:tw-bg-red-700 tw-text-white"
                        handleClick={() => {
                            if (propertyToDelete?.id) {
                                const idToDelete = propertyToDelete.id;
                                deletePropertyMutation.mutate(idToDelete);
                            }
                        }}
                        text={(deletePropertyMutation as any).isPending ? "Deleting..." : "Delete"}
                        isDisabled={(deletePropertyMutation as any).isPending}
                        dataCy="delete-confirm-btn"
                        id="delete-confirm-btn"
                    />
                </Modal.Footer>
            </Modal>
        </div>
    );
}
